import { useState } from 'react';
import { Box, Button, Center, Group, Loader, Table, Text, TextInput, Tooltip, SegmentedControl, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { DatePickerInput } from '@mantine/dates';
import { IconFileSpreadsheet, IconSearch, IconX } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { StaffLogModal, type StaffLogTarget } from './StaffLogModal';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { Specialty } from './Specialty';
import { FetchOverlay } from './FetchOverlay';
import { eksportXlsx, type Ustun } from '../lib/xlsx';

interface KunYozuv { logId: string; soni: number; checkIn: string; checkOut: string | null; daqiqa: number }

interface Qator {
  user: { id: string; fullName: string; role: string; specialty?: string | null };
  keldi: number;
  kelmadi: number;
  daqiqa: number;
  kunlar: Record<string, KunYozuv>;
}

interface Javob {
  from: string;
  to: string;
  /** Oraliqdagi barcha kalendar kunlari ("YYYY-MM-DD") — jadval ustunlari. */
  sanalar: string[];
  kunSoni: number;
  ishKunlari: string[];
  bayramKunlari: number;
  otganIshKunlari: number;
  qatorlar: Qator[];
}

const kalit = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const bugunKalit = () => kalit(new Date());

type Tezkor = 'thisMonth' | 'lastMonth' | 'thisWeek';
/** Tezkor oraliqlar — eng ko'p kerak bo'ladiganlari (oy oxiri hisobi, o'tgan oy, shu hafta). */
function tezkorOraliq(v: Tezkor): [Date, Date] {
  const b = new Date();
  if (v === 'lastMonth') return [new Date(b.getFullYear(), b.getMonth() - 1, 1), new Date(b.getFullYear(), b.getMonth(), 0)];
  if (v === 'thisWeek') {
    const dush = new Date(b.getFullYear(), b.getMonth(), b.getDate() - ((b.getDay() + 6) % 7));
    return [dush, new Date(dush.getFullYear(), dush.getMonth(), dush.getDate() + 6)];
  }
  return [new Date(b.getFullYear(), b.getMonth(), 1), new Date(b.getFullYear(), b.getMonth() + 1, 0)];
}
/** Ustun sarlavhasi: kun raqami; oy almashganda (yoki birinchi ustunda, oraliq bir necha oyni olsa) "1.10". */
const ustunNomi = (k: string, i: number, kopOy: boolean) => {
  const kun = Number(k.slice(8));
  return kopOy && (i === 0 || kun === 1) ? `${kun}.${k.slice(5, 7)}` : String(kun);
};
const soat = (v?: string | null) =>
  v ? new Date(v).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—';
/** Daqiqa → "152:30" (soat:daqiqa). */
const soatDaqiqa = (m: number) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;

/**
 * Xodimlar davomati — OYLIK hisobot.
 *
 * Kunlik sahifa bitta kunni ko'rsatadi; oy oxirida esa "kim necha kun keldi,
 * necha kun kelmadi, qancha ishladi" degan savol turadi (maosh, jarima). Ilgari
 * buning uchun har kunni alohida ochib sanash kerak edi.
 *
 * Ish kuni / dam olish kuni — SERVER aytadi (`ishKunlari`), brauzer hafta kunini
 * o'zi sanamaydi: maktab shanba ishlashi mumkin, ustiga bayramlar bor.
 */
export function StaffAttendanceMonth() {
  const { t } = useTranslation();
  /* SANA ORALIG'I — oy chegarasiga bog'lanmagan: "15-sentabrdan 14-oktabrgacha"
     kabi hisobot ham kerak bo'ladi. Sukut bo'yicha — joriy oy. */
  const [oraliq, setOraliq] = useState<[Date | null, Date | null]>(() => tezkorOraliq('thisMonth'));
  const [qidiruv, setQidiruv] = useState('');
  const from = oraliq[0] ? kalit(oraliq[0]) : null;
  const to = oraliq[1] ? kalit(oraliq[1]) : null;
  const tezkor = (['thisMonth', 'lastMonth', 'thisWeek'] as Tezkor[]).find((v) => {
    const [a, b] = tezkorOraliq(v);
    return kalit(a) === from && kalit(b) === to;
  }) ?? null;
  const qc = useQueryClient();
  const theme = useMantineTheme();
  const telefon = !!useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const [tahrir, setTahrir] = useState<StaffLogTarget | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['staff-attendance-month', from, to],
    queryFn: async () => (await api.get<Javob>('/payroll/logs/month', { params: { from, to } })).data,
    // Oraliqning ikkinchi sanasi tanlanmaguncha so'rov yuborilmaydi
    enabled: !!from && !!to,
  });

  const qatorlar = data?.qatorlar ?? [];
  const izla = qidiruv.trim().toLowerCase();
  const topilgan = izla
    ? qatorlar.filter(
        (x) =>
          x.user.fullName.toLowerCase().includes(izla) ||
          t(`roles.${x.user.role}`).toLowerCase().includes(izla)
      )
    : qatorlar;

  const ishKuni = new Set(data?.ishKunlari ?? []);
  const bugun = bugunKalit();
  const kunlar = data?.sanalar ?? [];
  const kopOy = !!data && data.from.slice(0, 7) !== data.to.slice(0, 7);

  const [yuklanmoqda, setYuklanmoqda] = useState(false);
  const eksport = async () => {
    setYuklanmoqda(true);
    try {
      const ustunlar: Ustun<Qator>[] = [
        { key: 'fullName', label: t('staffAttendance.staff'), get: (q) => q.user.fullName },
        { key: 'role', label: t('staffAttendance.role'), get: (q) => t(`roles.${q.user.role}`) },
        ...kunlar.map((k) => ({
          key: k,
          label: k.split('-').reverse().join('.'),
          get: (q: Qator) => {
            const y = q.kunlar[k];
            if (y) return `${soat(y.checkIn)}–${soat(y.checkOut)}`;
            return ishKuni.has(k) && k <= bugun ? '✗' : '';
          },
        })),
        { key: 'keldi', label: t('staffAttendance.daysIn'), get: (q) => q.keldi },
        { key: 'kelmadi', label: t('staffAttendance.daysOut'), get: (q) => q.kelmadi },
        { key: 'soat', label: t('staffAttendance.hours'), get: (q) => soatDaqiqa(q.daqiqa) },
      ];
      await eksportXlsx({ filename: `xodimlar-davomati-${from}_${to}`, ustunlar, qatorlar: topilgan });
    } finally {
      setYuklanmoqda(false);
    }
  };

  return (
    <Box pos="relative">
      <FetchOverlay visible={isFetching && !isLoading} />

      <Group p="md" pb="xs" justify="space-between" align="flex-end">
        <Group align="flex-end" w={{ base: '100%', sm: 'auto' }}>
          <DatePickerInput
            type="range"
            label={t('staffAttendance.range')}
            value={oraliq}
            onChange={setOraliq}
            valueFormat="DD.MM.YYYY"
            allowSingleDateInRange
            w={{ base: '100%', sm: 230 }}
          />
          <SegmentedControl
            size="xs"
            fullWidth={telefon}
            w={{ base: '100%', sm: 'auto' }}
            value={tezkor ?? ''}
            onChange={(v) => setOraliq(tezkorOraliq(v as Tezkor))}
            data={[
              { value: 'thisWeek', label: t('staffAttendance.thisWeek') },
              { value: 'thisMonth', label: t('staffAttendance.thisMonth') },
              { value: 'lastMonth', label: t('staffAttendance.lastMonth') },
            ]}
          />
          <TextInput
            label={t('common.search')}
            placeholder={t('staffAttendance.searchPlaceholder')}
            value={qidiruv}
            onChange={(e) => setQidiruv(e.currentTarget.value)}
            leftSection={<IconSearch size={15} />}
            rightSection={
              qidiruv ? <IconX size={15} style={{ cursor: 'pointer' }} onClick={() => setQidiruv('')} /> : null
            }
            w={{ base: '100%', sm: 240 }}
          />
        </Group>
        <Group gap="sm">
          {data && (
            <Text size="sm" c="dimmed">
              {t('staffAttendance.monthSummary', { ish: data.ishKunlari.length, otgan: data.otganIshKunlari })}
            </Text>
          )}
          <Button
            size="sm"
            variant="light"
            leftSection={<IconFileSpreadsheet size={16} />}
            loading={yuklanmoqda}
            disabled={!topilgan.length}
            onClick={eksport}
          >
            XLSX
          </Button>
        </Group>
      </Group>

      <Text size="xs" c="dimmed" px="md" pb="xs">
        {t('staffAttendance.legend')}. {t('staffAttendance.cellHint')}.
      </Text>

      {isLoading ? (
        <Center h={180}><Loader /></Center>
      ) : qatorlar.length === 0 ? (
        <Text size="sm" c="dimmed" p="md">{t('staffAttendance.empty')}</Text>
      ) : topilgan.length === 0 ? (
        <Text size="sm" c="dimmed" p="md">{t('common.noMatch')}</Text>
      ) : (
        <Table.ScrollContainer minWidth={640}>
          <Table withColumnBorders highlightOnHover verticalSpacing={4} horizontalSpacing={4} fz="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--mantine-color-body)', minWidth: telefon ? 120 : 200 }}>
                  {t('staffAttendance.staff')}
                </Table.Th>
                <Table.Th ta="center" style={{ whiteSpace: 'nowrap' }}>{t('staffAttendance.daysIn')}</Table.Th>
                <Table.Th ta="center" style={{ whiteSpace: 'nowrap' }}>{t('staffAttendance.daysOut')}</Table.Th>
                <Table.Th ta="center" style={{ whiteSpace: 'nowrap' }}>{t('staffAttendance.hours')}</Table.Th>
                {kunlar.map((k, i) => (
                  <Table.Th key={k} ta="center" c={ishKuni.has(k) ? undefined : 'dimmed'} style={{ minWidth: 26, whiteSpace: 'nowrap' }}>
                    {ustunNomi(k, i, kopOy)}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {topilgan.map((q) => (
                <Table.Tr key={q.user.id}>
                  <Table.Td fw={500} style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--mantine-color-body)', whiteSpace: telefon ? 'normal' : 'nowrap', maxWidth: telefon ? 140 : undefined }}>
                    {q.user.fullName}<Specialty value={q.user.specialty} />
                  </Table.Td>
                  <Table.Td ta="center" fw={600} c="teal">{q.keldi}</Table.Td>
                  <Table.Td ta="center" fw={600} c={q.kelmadi ? 'red' : 'dimmed'}>{q.kelmadi}</Table.Td>
                  <Table.Td ta="center" ff="monospace">{soatDaqiqa(q.daqiqa)}</Table.Td>
                  {kunlar.map((k) => {
                    const y = q.kunlar[k];
                    const ish = ishKuni.has(k);
                    const kelmadi = !y && ish && k <= bugun;
                    const fon = y
                      ? 'var(--mantine-color-teal-light)'
                      : kelmadi
                        ? 'var(--mantine-color-red-light)'
                        : !ish
                          ? 'var(--mantine-color-default-hover)'
                          : undefined;
                    /* Bosiladigan katak: yozuv bor kun (tahrirlash) yoki o'tgan ish kuni (qo'shish).
                       Dam olish va kelajak kunlari bosilmaydi — server baribir rad etadi. */
                    const bosiladi = !!y || (ish && k <= bugun);
                    const katak = (
                      <Table.Td
                        key={k}
                        ta="center"
                        style={{ background: fon, cursor: bosiladi ? 'pointer' : undefined }}
                        onClick={
                          bosiladi
                            ? () => setTahrir({
                                user: q.user,
                                sana: k,
                                log: y ? { id: y.logId, checkIn: y.checkIn, checkOut: y.checkOut } : null,
                              })
                            : undefined
                        }
                      >
                        {y ? (y.checkOut ? '✓' : '•') : kelmadi ? '✗' : ''}
                      </Table.Td>
                    );
                    return y ? (
                      <Tooltip
                        key={k}
                        withArrow
                        label={`${soat(y.checkIn)} – ${soat(y.checkOut)}${y.soni > 1 ? ` (${y.soni})` : ''}`}
                      >
                        {katak}
                      </Tooltip>
                    ) : katak;
                  })}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <StaffLogModal
        target={tahrir}
        onClose={() => setTahrir(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ['staff-attendance-month'] });
          qc.invalidateQueries({ queryKey: ['staff-attendance'] });
        }}
      />
    </Box>
  );
}
